import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, Label, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { TopBar } from 'components/organisms';
import TableDraggableSortableList from 'components/molecules/table-draggable-items/sortable-list/TableDraggableSortableList';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useTranslation } from 'react-i18next';
import {
  PiNavigationArrow,
  PiTrash,
  PiUserCircleDashed,
  PiWarning,
} from 'react-icons/pi';
import { useGetArtistLightList } from 'services/artists/light';
import { usePostProductReplaceDuplicateArtists } from 'services/music-product-digital';
import { ITrackMutationForm } from 'services/tracks/types';
import { ARTIST_ROLE } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getArtistsWithRolePlacement } from 'middleware/track/artistsWithRolePlacement';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const TrackArtists = () => {
  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<ITrackMutationForm> =
    useFormikContext<ITrackMutationForm>();

  const { getSelectOptions } = useEnumToSelectOptions();

  const artistsLight = useGetArtistLightList();

  const { t } = useTranslation();

  const replaceDuplicates = usePostProductReplaceDuplicateArtists();

  const onReplaceDuplicates = async () =>
    await replaceDuplicates.mutateAsync(
      getArtistsWithRolePlacement(values.artists),
      {
        onSuccess: data =>
          setFieldValue(
            'artists',
            data.map(artist => ({
              id: artist.artistId,
              role: artist.artistRole,
              nickname: artist.nickname ?? '',
            }))
          ),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );

  const shouldShowReplaceDuplicates = !!values.hasDuplicatedArtist;
  const shouldShowTopBar = shouldShowReplaceDuplicates;

  const artistsSelectOptions = artistsLight.data?.map(artist => ({
    label: artist.nickname,
    value: artist.id,
  }));

  const navigateToArtist = (id: string) => {
    window.open(`/artist/${id}/external`, '_blank');
  };

  const addArtist = () =>
    setFieldValue(`artists[${values.artists.length}]`, {
      id: '',
      nickname: '',
      role: ARTIST_ROLE.unknown,
    });

  const removeArtist = (id: string, role: ARTIST_ROLE) => {
    const removeIndex = values.artists.findIndex(
      artist => artist.role === role && artist.id === id
    );

    const newArtists = values.artists;

    newArtists.splice(removeIndex, 1);

    setFieldValue('artists', newArtists);
  };

  const getFilteredRoleSelectOptionByArtist = (id: string) => {
    const selectOptions = getSelectOptions(ARTIST_ROLE, 'enum.ARTIST_ROLE');

    const artistSelectedRoles = values.artists
      .filter(artist => artist.id === id)
      .map(artist => artist.role);

    return selectOptions
      .filter(({ value }) =>
        values.isInstrumental ? value !== ARTIST_ROLE.lyricist : true
      )
      .filter(({ value }) => !artistSelectedRoles.includes(value));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const movedArray = arrayMove(
        values.artists,
        parseInt(`${active.id}`) - 1,
        parseInt(`${over.id}`) - 1
      );

      setFieldValue('artists', movedArray);
    }
    document.body.style.setProperty('cursor', '');
  };

  return (
    <>
      {!status?.displayMode && shouldShowTopBar && (
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex items-center gap-2'>
              {shouldShowReplaceDuplicates && (
                <Button
                  variant={BUTTON_VARIANT.default}
                  onClick={onReplaceDuplicates}
                  isLoading={replaceDuplicates.isPending}>
                  <Button.Icon icon={<PiUserCircleDashed />} />
                  <Button.Text text={t('button.replace-duplicates')} />
                </Button>
              )}
            </div>
          }
        />
      )}

      {status?.displayMode && values.hasDuplicatedArtist && (
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex items-center gap-2 text-red'>
              <PiUserCircleDashed className='text-lg' />
              <span className='text-base font-semibold'>
                {t('toast-message.duplicated-artist-error')}
              </span>
            </div>
          }
        />
      )}

      <TableDraggableSortableList
        displayMode={status?.displayMode}
        handleDragEnd={handleDragEnd}
        onAdd={addArtist}
        size='compact'
        data={{
          nodes: values.artists.map((artist, index) => ({
            id: index + 1,
            index: index,
            artistId: artist.id,
            role: artist.role,
            nickname: artist.nickname,
            isDuplicated: artist.isDuplicated,
          })),
        }}
        columns={[
        {
          label: t(`input.label.nickname`),
          renderCell: ({ nickname, index, artistId }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={`${nickname}`} />
              ) : (
                <FormInput.Select
                  name={`artists[${index}].id`}
                  placeholder={t('input.label.nickname')}
                  label={t(`input.label.artist`)}
                  onChange={value => {
                    setFieldValue(`artists[${index}].nickname`, value.label);
                  }}
                  value={{
                    label: nickname,
                    value: artistId,
                  }}
                  options={artistsSelectOptions}
                  isLoading={artistsLight.isLoading}
                  isAsync
                  isSearchable
                />
              )}
            </TableItem>
          ),
        },

        {
          label: t(`input.label.role`),
          renderCell: ({ role, index, artistId }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text
                  text={t(`enum.ARTIST_ROLE.${ARTIST_ROLE[role]}`)}
                />
              ) : (
                <FormInput.Select
                  name={`artists[${index}].role`}
                  label={t(`input.label.role`)}
                  value={{
                    label: t(`enum.ARTIST_ROLE.${ARTIST_ROLE[role]}`),
                    value: role,
                  }}
                  options={getFilteredRoleSelectOptionByArtist(artistId)}
                  isDisabled={!values.artists[index].id}
                />
              )}
            </TableItem>
          ),
        },
        {
          label: '',
          renderCell: ({ index, isDuplicated }) => (
            <TableItem>
              {isDuplicated && (
                <Label color='red' size='compact'>
                  <PiUserCircleDashed />
                  {t('music-product-digital.artists.is-duplicated')}
                </Label>
              )}
            </TableItem>
          ),
        },
        {
          label: '',
          renderCell: ({ artistId, role }) => (
            <TableItem justifyPosition='end'>
              {status?.displayMode ? (
                <Button
                  isClear
                  onlyIcon
                  onClick={() => navigateToArtist(artistId)}>
                                    <Button.Description description={t('button.open')} />

                                    <Button.Icon
                    icon={<PiNavigationArrow transform='scale(-1,1)' />}
                  />
                </Button>
              ) : (
                <Button
                  variant={BUTTON_VARIANT.remove}
                  isClear
                  onlyIcon
                  onClick={() => removeArtist(artistId, role)}>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </TableItem>
          ),
        },
      ]}
    />
    </>
  );
};

export default TrackArtists;
